/**
 * CNPJ mask + check-digit validation for the dashboard's onboarding CNPJ
 * step (TTP-243 — Tela 2). This is a client-side REPLICA of the mod-11
 * algorithm in apps/api/src/modules/tenancy/cnpj.ts — deliberately not
 * imported from there (apps/dashboard never depends on apps/api; see
 * CLAUDE.md module boundaries). Keep both in sync by hand if the algorithm
 * ever changes — it's a stable, standardized Receita Federal algorithm, not
 * expected to.
 */

const FIRST_CHECK_DIGIT_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const SECOND_CHECK_DIGIT_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;

// Mathematically pass the check-digit algorithm but are not real CNPJs
// (all 14 digits identical) — must be rejected explicitly.
const KNOWN_INVALID_SEQUENCES = new Set([
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999',
]);

/** Strips any non-digit character (dots, slash, dash, whitespace). */
export function normalizeCnpj(input: string): string {
  return input.replace(/\D/g, '');
}

function calculateCheckDigit(digits: readonly number[], weights: readonly number[]): number {
  const sum = digits.reduce((acc, digit, index) => acc + digit * (weights[index] ?? 0), 0);
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

/**
 * Validates a CNPJ's two check digits. Accepts either a formatted
 * (`11.222.333/0001-81`) or raw (`11222333000181`) string — normalizes
 * internally. Returns `false` for anything that isn't exactly 14 digits
 * after normalization, or that fails either check digit.
 */
export function isValidCnpj(rawInput: string): boolean {
  const digits = normalizeCnpj(rawInput);

  if (digits.length !== 14 || KNOWN_INVALID_SEQUENCES.has(digits)) {
    return false;
  }

  const numbers = digits.split('').map(Number);
  const base = numbers.slice(0, 12);
  const providedFirstCheckDigit = numbers[12];
  const providedSecondCheckDigit = numbers[13];

  const firstCheckDigit = calculateCheckDigit(base, FIRST_CHECK_DIGIT_WEIGHTS);
  if (firstCheckDigit !== providedFirstCheckDigit) {
    return false;
  }

  const secondCheckDigit = calculateCheckDigit([...base, firstCheckDigit], SECOND_CHECK_DIGIT_WEIGHTS);
  return secondCheckDigit === providedSecondCheckDigit;
}

/**
 * Formats up-to-14 raw digits progressively as `00.000.000/0000-00` — safe
 * to call on every keystroke of a controlled `<input>` (partial input formats
 * partially, never throws). Extra digits beyond 14 are dropped.
 */
export function maskCnpj(input: string): string {
  const digits = normalizeCnpj(input).slice(0, 14);

  let result = digits.slice(0, 2);
  if (digits.length > 2) result += `.${digits.slice(2, 5)}`;
  if (digits.length > 5) result += `.${digits.slice(5, 8)}`;
  if (digits.length > 8) result += `/${digits.slice(8, 12)}`;
  if (digits.length > 12) result += `-${digits.slice(12, 14)}`;
  return result;
}
